import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import { API_BASE_URL } from "../../config";

const Dashboard = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [activeBlog, setActiveBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState("");

    // Blog details state
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [coverImage, setCoverImage] = useState("");
    const [isPublished, setIsPublished] = useState(false);
    const [scheduledDate, setScheduledDate] = useState("");
    const [blocks, setBlocks] = useState([]);

    // Check auth
    useEffect(() => {
        const token = localStorage.getItem("admin_token");
        if (!token) {
            navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
            return;
        }

        fetchBlogs(token);
    }, [navigate]);

    const fetchBlogs = async (token) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/blogs`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem("admin_token");
                navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
                return;
            }
            const data = await response.json();
            setBlogs(data);
        } catch (err) {
            setError("Failed to fetch blogs.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
    };

    // Client-side image compression to WebP
    const compressToWebp = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    let width = img.width;
                    let height = img.height;
                    const maxDim = 1600;

                    if (width > maxDim || height > maxDim) {
                        if (width > height) {
                            height = Math.round((height * maxDim) / width);
                            width = maxDim;
                        } else {
                            width = Math.round((width * maxDim) / height);
                            height = maxDim;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            return reject(new Error("Compression failed"));
                        }
                        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                        resolve(new File([blob], `${nameWithoutExt}.webp`, { type: "image/webp" }));
                    }, "image/webp", 0.75);
                };
                img.onerror = (err) => reject(err);
            };
            reader.onerror = (err) => reject(err);
        });
    };

    // Universal Upload Handler
    const uploadImage = async (file) => {
        const token = localStorage.getItem("admin_token");
        try {
            const compressed = await compressToWebp(file);
            const formData = new FormData();
            formData.append("image", compressed);

            const response = await fetch(`${API_BASE_URL}/api/upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Upload failed");
            return data.url;
        } catch (err) {
            alert(err.message || "Failed to upload image.");
            return "";
        }
    };

    const handleCoverImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const url = await uploadImage(file);
        if (url) setCoverImage(url);
    };

    const handleBlockImageChange = async (index, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const url = await uploadImage(file);
        if (url) {
            updateBlock(index, "url", url);
        }
    };

    // Auto-slugify helper
    const handleTitleChange = (val) => {
        setTitle(val);
        // Only auto-update slug if creating new
        if (!activeBlog) {
            setSlug(
                val
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, "")
                    .replace(/\s+/g, "-")
                    .replace(/-+/g, "-")
                    .trim()
            );
        }
    };

    // Editor block utilities
    const addBlock = (type) => {
        const newBlock = {
            id: Math.random().toString(36).substring(2, 9),
            type,
            content: "",
            isTimelineAnchor: false,
            anchorLabel: "",
            anchorId: "",
            listType: "bullet",
            items: type === "list" ? [""] : [],
            headers: type === "table" ? ["Col 1", "Col 2"] : [],
            rows: type === "table" ? [["", ""]] : [],
            url: "",
            caption: "",
            year: "",
            milestoneTitle: "",
            milestoneDesc: "",
        };
        setBlocks([...blocks, newBlock]);
    };

    const updateBlock = (index, field, value) => {
        const updated = [...blocks];
        updated[index] = { ...updated[index], [field]: value };

        // Auto generate anchorId if timeline anchor is checked
        if (field === "anchorLabel" && updated[index].isTimelineAnchor) {
            updated[index].anchorId = value
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, "")
                .replace(/\s+/g, "-")
                .replace(/-+/g, "-")
                .trim();
        }
        if (field === "isTimelineAnchor" && value === true && !updated[index].anchorId) {
            updated[index].anchorId = updated[index].anchorLabel
                ? updated[index].anchorLabel.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-")
                : `section-${updated[index].id}`;
        }

        setBlocks(updated);
    };

    const deleteBlock = (index) => {
        setBlocks(blocks.filter((_, i) => i !== index));
    };

    const moveBlock = (index, direction) => {
        if (direction === "up" && index === 0) return;
        if (direction === "down" && index === blocks.length - 1) return;

        const updated = [...blocks];
        const swapIndex = direction === "up" ? index - 1 : index + 1;
        const temp = updated[index];
        updated[index] = updated[swapIndex];
        updated[swapIndex] = temp;
        setBlocks(updated);
    };

    // List Block helpers
    const addListItem = (blockIdx) => {
        const updated = [...blocks];
        updated[blockIdx].items.push("");
        setBlocks(updated);
    };

    const updateListItem = (blockIdx, itemIdx, val) => {
        const updated = [...blocks];
        updated[blockIdx].items[itemIdx] = val;
        setBlocks(updated);
    };

    const removeListItem = (blockIdx, itemIdx) => {
        const updated = [...blocks];
        updated[blockIdx].items.splice(itemIdx, 1);
        setBlocks(updated);
    };

    // Table Block helpers
    const addTableRow = (blockIdx) => {
        const updated = [...blocks];
        const colCount = updated[blockIdx].headers.length;
        updated[blockIdx].rows.push(Array(colCount).fill(""));
        setBlocks(updated);
    };

    const addTableCol = (blockIdx) => {
        const updated = [...blocks];
        updated[blockIdx].headers.push(`Col ${updated[blockIdx].headers.length + 1}`);
        updated[blockIdx].rows = updated[blockIdx].rows.map(row => [...row, ""]);
        setBlocks(updated);
    };

    const updateTableCell = (blockIdx, rowIdx, colIdx, val) => {
        const updated = [...blocks];
        updated[blockIdx].rows[rowIdx][colIdx] = val;
        setBlocks(updated);
    };

    const updateTableHeader = (blockIdx, colIdx, val) => {
        const updated = [...blocks];
        updated[blockIdx].headers[colIdx] = val;
        setBlocks(updated);
    };

    const removeTableRow = (blockIdx) => {
        const updated = [...blocks];
        if (updated[blockIdx].rows.length > 1) {
            updated[blockIdx].rows.pop();
            setBlocks(updated);
        }
    };

    const removeTableCol = (blockIdx) => {
        const updated = [...blocks];
        if (updated[blockIdx].headers.length > 1) {
            updated[blockIdx].headers.pop();
            updated[blockIdx].rows = updated[blockIdx].rows.map(row => {
                row.pop();
                return row;
            });
            setBlocks(updated);
        }
    };

    // Start editing/creating
    const startNewBlog = () => {
        setActiveBlog(null);
        setTitle("");
        setSlug("");
        setExcerpt("");
        setCoverImage("");
        setIsPublished(false);
        setScheduledDate("");
        setBlocks([]);
        setIsEditing(true);
    };

    const editBlog = (blog) => {
        setActiveBlog(blog);
        setTitle(blog.title);
        setSlug(blog.slug);
        setExcerpt(blog.excerpt || "");
        setCoverImage(blog.coverImage || "");
        setIsPublished(blog.isPublished);
        setScheduledDate(
            blog.scheduledDate ? new Date(blog.scheduledDate).toISOString().substring(0, 16) : ""
        );
        setBlocks(blog.blocks || []);
        setIsEditing(true);
    };

    const deleteBlogCall = async (id) => {
        if (!window.confirm("Are you sure you want to delete this blog?")) return;
        const token = localStorage.getItem("admin_token");
        try {
            const res = await fetch(`${API_BASE_URL}/api/blogs/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (res.ok) {
                setBlogs(blogs.filter(b => b._id !== id));
            } else {
                alert("Failed to delete blog.");
            }
        } catch (err) {
            alert("Error connecting to server.");
        }
    };

    const saveBlog = async (e) => {
        e.preventDefault();
        if (!title || !slug) {
            alert("Title and Slug are required.");
            return;
        }

        setSaveLoading(true);
        const token = localStorage.getItem("admin_token");
        const payload = {
            title,
            slug: slug.toLowerCase().replace(/\s+/g, "-"),
            excerpt,
            coverImage,
            blocks,
            isPublished,
            scheduledDate: scheduledDate || null,
        };

        const url = activeBlog 
            ? `${API_BASE_URL}/api/blogs/${activeBlog._id}`
            : `${API_BASE_URL}/api/blogs`;
        const method = activeBlog ? "PUT" : "POST";

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || "Failed to save blog.");
            }

            setIsEditing(false);
            fetchBlogs(token);
        } catch (err) {
            alert(err.message);
        } finally {
            setSaveLoading(false);
        }
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-sidebar">
                <div className="sidebar-logo">Anubhava</div>
                <ul className="sidebar-menu">
                    <li className="sidebar-item active">
                        <i className="fa-solid fa-blog"></i>
                        <span>Manage Blogs</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/students")}>
                        <i className="fa-solid fa-users"></i>
                        <span>Students CRM</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/gallery")}>
                        <i className="fa-solid fa-images"></i>
                        <span>Gallery</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/gallery")}>
                        <i className="fa-solid fa-globe"></i>
                        <span>Visit Website</span>
                    </li>
                </ul>
                <div className="sidebar-logout">
                    <button onClick={handleLogout} className="btn-secondary" style={{ width: "100%" }}>Log Out</button>
                </div>
            </div>

            <div className="dashboard-content">
                {isEditing ? (
                    <div>
                        <div className="dashboard-header">
                            <h1>{activeBlog ? "Edit Blog" : "Create New Blog"}</h1>
                            <button className="btn-secondary" onClick={() => setIsEditing(false)}>Back to List</button>
                        </div>

                        <form onSubmit={saveBlog} className="editor-form-wrapper">
                            <div className="editor-row">
                                <div className="editor-main-inputs">
                                    <div className="form-group">
                                        <label>Blog Title</label>
                                        <input
                                            type="text"
                                            placeholder="Write an engaging title..."
                                            value={title}
                                            onChange={(e) => handleTitleChange(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Custom Link Slug</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. importance-of-sensorial-activities"
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Blog Excerpt</label>
                                        <textarea
                                            placeholder="Write a brief preview sentence summarizing this post..."
                                            rows="3"
                                            value={excerpt}
                                            onChange={(e) => setExcerpt(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="editor-meta-inputs">
                                    <div className="form-group">
                                        <label>Cover Banner Image</label>
                                        {coverImage ? (
                                            <div className="image-preview-container">
                                                <img src={coverImage} alt="Cover Banner" />
                                                <button type="button" className="remove-img-btn" onClick={() => setCoverImage("")}>
                                                    <i className="fas fa-trash-can"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="image-upload-area" onClick={() => document.getElementById("cover-uploader").click()}>
                                                <i className="fa-solid fa-cloud-arrow-up upload-icon"></i>
                                                <p>Upload Banner Image (auto-webp)</p>
                                                <input
                                                    type="file"
                                                    id="cover-uploader"
                                                    accept="image/*"
                                                    onChange={handleCoverImageChange}
                                                    style={{ display: "none" }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group" style={{ flexDirection: "row", gap: "10px", alignItems: "center" }}>
                                        <input
                                            type="checkbox"
                                            id="isPublished"
                                            checked={isPublished}
                                            onChange={(e) => setIsPublished(e.target.checked)}
                                        />
                                        <label htmlFor="isPublished" style={{ cursor: "pointer" }}>Publish Publicly</label>
                                    </div>

                                    <div className="form-group">
                                        <label>Schedule Publication Date & Time</label>
                                        <input
                                            type="datetime-local"
                                            value={scheduledDate}
                                            onChange={(e) => setScheduledDate(e.target.value)}
                                        />
                                        <small style={{ color: "#777" }}>Keep blank to publish immediately on saving.</small>
                                    </div>
                                </div>
                            </div>

                            {/* Blocks workspace */}
                            <div className="blocks-workspace">
                                <h3>Blog Body Content Blocks</h3>
                                <div className="blocks-list">
                                    {blocks.map((block, idx) => (
                                        <div key={block.id} className="editor-block-card">
                                            <div className="block-header-toolbar">
                                                <span className="block-label">{block.type}</span>
                                                <div className="block-header-actions">
                                                    <button type="button" className="block-action-btn" onClick={() => moveBlock(idx, "up")}>
                                                        <i className="fa-solid fa-arrow-up"></i>
                                                    </button>
                                                    <button type="button" className="block-action-btn" onClick={() => moveBlock(idx, "down")}>
                                                        <i className="fa-solid fa-arrow-down"></i>
                                                    </button>
                                                    <button type="button" className="block-action-btn delete-block" onClick={() => deleteBlock(idx)}>
                                                        <i className="fa-solid fa-trash-can"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="block-content-body">
                                                {block.type === "heading" && (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Heading Text..."
                                                            value={block.content}
                                                            onChange={(e) => updateBlock(idx, "content", e.target.value)}
                                                            className="form-group input"
                                                            style={{ width: "100%", padding: "10px", fontSize: "18px", fontWeight: "bold" }}
                                                        />
                                                        <div className="block-anchor-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                id={`anchor-${block.id}`}
                                                                checked={block.isTimelineAnchor}
                                                                onChange={(e) => updateBlock(idx, "isTimelineAnchor", e.target.checked)}
                                                            />
                                                            <label htmlFor={`anchor-${block.id}`}>Link to Timeline navigation</label>
                                                        </div>

                                                        {block.isTimelineAnchor && (
                                                            <div className="block-anchor-input-row">
                                                                <div className="form-group">
                                                                    <label>Timeline Label</label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Label e.g. Early Childhood"
                                                                        value={block.anchorLabel}
                                                                        onChange={(e) => updateBlock(idx, "anchorLabel", e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>Generated Element ID</label>
                                                                    <input type="text" value={block.anchorId} readOnly disabled />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {block.type === "subheading" && (
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Subheading text..."
                                                        value={block.content}
                                                        onChange={(e) => updateBlock(idx, "content", e.target.value)}
                                                        style={{ width: "100%", padding: "10px", fontSize: "16px", fontWeight: "600" }}
                                                    />
                                                )}

                                                {block.type === "paragraph" && (
                                                    <textarea
                                                        placeholder="Enter paragraph content..."
                                                        rows="4"
                                                        value={block.content}
                                                        onChange={(e) => updateBlock(idx, "content", e.target.value)}
                                                        style={{ width: "100%", padding: "10px", fontSize: "15px", fontFamily: "inherit" }}
                                                    />
                                                )}

                                                {block.type === "list" && (
                                                    <div className="list-editor">
                                                        <select
                                                            value={block.listType}
                                                            onChange={(e) => updateBlock(idx, "listType", e.target.value)}
                                                            style={{ marginBottom: "10px" }}
                                                        >
                                                            <option value="bullet">Bulleted List</option>
                                                            <option value="ordered">Numbered List</option>
                                                        </select>
                                                        <div className="list-editor-list">
                                                            {block.items.map((item, itemIdx) => (
                                                                <div key={itemIdx} className="list-item-row">
                                                                    <span>{block.listType === "bullet" ? "•" : `${itemIdx + 1}.`}</span>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Item description..."
                                                                        value={item}
                                                                        onChange={(e) => updateListItem(idx, itemIdx, e.target.value)}
                                                                    />
                                                                    <button type="button" className="block-action-btn delete-block" onClick={() => removeListItem(idx, itemIdx)}>
                                                                        <i className="fa-solid fa-xmark"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <button type="button" className="table-btn" style={{ marginTop: "10px" }} onClick={() => addListItem(idx)}>
                                                            + Add List Item
                                                        </button>
                                                    </div>
                                                )}

                                                {block.type === "table" && (
                                                    <div className="editor-table-manager">
                                                        <div className="table-editor-grid">
                                                            <table>
                                                                <thead>
                                                                    <tr>
                                                                        {block.headers.map((hdr, colIdx) => (
                                                                            <th key={colIdx}>
                                                                                <input
                                                                                    type="text"
                                                                                    value={hdr}
                                                                                    onChange={(e) => updateTableHeader(idx, colIdx, e.target.value)}
                                                                                />
                                                                            </th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {block.rows.map((row, rowIdx) => (
                                                                        <tr key={rowIdx}>
                                                                            {row.map((cell, colIdx) => (
                                                                                <td key={colIdx}>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={cell}
                                                                                        onChange={(e) => updateTableCell(idx, rowIdx, colIdx, e.target.value)}
                                                                                    />
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="table-size-actions">
                                                            <button type="button" className="table-btn" onClick={() => addTableRow(idx)}>+ Add Row</button>
                                                            <button type="button" className="table-btn" onClick={() => removeTableRow(idx)}>- Remove Row</button>
                                                            <button type="button" className="table-btn" onClick={() => addTableCol(idx)}>+ Add Col</button>
                                                            <button type="button" className="table-btn" onClick={() => removeTableCol(idx)}>- Remove Col</button>
                                                        </div>
                                                    </div>
                                                )}

                                                {block.type === "image" && (
                                                    <div className="block-image-uploader">
                                                        {block.url ? (
                                                            <div>
                                                                <img src={block.url} alt="Uploaded block element" style={{ maxWidth: "100%", maxHeight: "250px", objectFit: "contain", borderRadius: "8px" }} />
                                                                <button type="button" className="btn-secondary" style={{ display: "block", margin: "10px auto" }} onClick={() => updateBlock(idx, "url", "")}>Replace Image</button>
                                                            </div>
                                                        ) : (
                                                            <div className="image-upload-area" onClick={() => document.getElementById(`block-img-${idx}`).click()}>
                                                                <i className="fa-solid fa-cloud-arrow-up upload-icon"></i>
                                                                <p>Upload Image (auto-webp compression)</p>
                                                                <input
                                                                    type="file"
                                                                    id={`block-img-${idx}`}
                                                                    accept="image/*"
                                                                    onChange={(e) => handleBlockImageChange(idx, e)}
                                                                    style={{ display: "none" }}
                                                                />
                                                            </div>
                                                        )}
                                                        <input
                                                            type="text"
                                                            placeholder="Add caption (optional)..."
                                                            value={block.caption}
                                                            onChange={(e) => updateBlock(idx, "caption", e.target.value)}
                                                            style={{ width: "100%", padding: "8px", marginTop: "10px" }}
                                                        />
                                                    </div>
                                                )}

                                                {block.type === "timeline" && (
                                                    <div>
                                                        <div className="timeline-block-row">
                                                            <label>Milestone Year</label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. 1907"
                                                                value={block.year}
                                                                onChange={(e) => updateBlock(idx, "year", e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="timeline-block-row">
                                                            <label>Milestone Title</label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. Opening of first Casa dei Bambini"
                                                                value={block.milestoneTitle}
                                                                onChange={(e) => updateBlock(idx, "milestoneTitle", e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="form-group" style={{ marginTop: "10px" }}>
                                                            <label>Description</label>
                                                            <textarea
                                                                placeholder="Detail what happened during this milestone..."
                                                                rows="3"
                                                                value={block.milestoneDesc}
                                                                onChange={(e) => updateBlock(idx, "milestoneDesc", e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="add-block-toolbar">
                                    <p>Insert New Rich Media Block Element</p>
                                    <div className="add-buttons-container">
                                        <button type="button" className="add-btn" onClick={() => addBlock("heading")}>
                                            <i className="fa-solid fa-heading"></i> Heading Anchor
                                        </button>
                                        <button type="button" className="add-btn" onClick={() => addBlock("subheading")}>
                                            <i className="fa-solid fa-paragraph"></i> Sub Heading
                                        </button>
                                        <button type="button" className="add-btn" onClick={() => addBlock("paragraph")}>
                                            <i className="fa-solid fa-align-left"></i> Paragraph
                                        </button>
                                        <button type="button" className="add-btn" onClick={() => addBlock("list")}>
                                            <i className="fa-solid fa-list-ul"></i> Bullet/Ordered List
                                        </button>
                                        <button type="button" className="add-btn" onClick={() => addBlock("table")}>
                                            <i className="fa-solid fa-table"></i> Table Grid
                                        </button>
                                        <button type="button" className="add-btn" onClick={() => addBlock("image")}>
                                            <i className="fa-solid fa-image"></i> WebP Image
                                        </button>
                                        <button type="button" className="add-btn" onClick={() => addBlock("timeline")}>
                                            <i className="fa-solid fa-timeline"></i> Timeline Event
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="editor-footer-actions">
                                <button type="button" className="btn-secondary" onClick={() => setIsEditing(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={saveLoading}>{saveLoading ? "Saving Changes..." : "Save Blog Post"}</button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div>
                        <div className="dashboard-header">
                            <h1>Manage Blogs</h1>
                            <button className="new-blog-btn" onClick={startNewBlog}>
                                <i className="fa-solid fa-plus"></i> New Blog Post
                            </button>
                        </div>

                        {loading ? (
                            <p>Loading blogs...</p>
                        ) : error ? (
                            <div className="login-error-msg">{error}</div>
                        ) : blogs.length === 0 ? (
                            <div className="blogs-table-container no-blogs-prompt">
                                <h3>No blogs written yet</h3>
                                <p>Get started by clicking the "New Blog Post" button above.</p>
                            </div>
                        ) : (
                            <div className="blogs-table-container">
                                <table className="blogs-table">
                                    <thead>
                                        <tr>
                                            <th>Cover Banner</th>
                                            <th>Title</th>
                                            <th>Excerpt</th>
                                            <th>Slug</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blogs.map((blog) => (
                                            <tr key={blog._id}>
                                                <td>
                                                    {blog.coverImage ? (
                                                        <img src={blog.coverImage} alt="" style={{ width: "80px", height: "45px", objectFit: "cover", borderRadius: "4px" }} />
                                                    ) : (
                                                        <span style={{ color: "#aaa", fontSize: "12px" }}>No Banner</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="blog-row-title">{blog.title}</span>
                                                </td>
                                                <td>
                                                    <span style={{ fontSize: "13px", color: "#555" }}>
                                                        {blog.excerpt ? `${blog.excerpt.substring(0, 45)}...` : "-"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <code>{blog.slug}</code>
                                                </td>
                                                <td>
                                                    {blog.isPublished ? (
                                                        blog.scheduledDate && new Date(blog.scheduledDate) > new Date() ? (
                                                            <span className="blog-status-badge scheduled">Scheduled</span>
                                                        ) : (
                                                            <span className="blog-status-badge published">Published</span>
                                                        )
                                                    ) : (
                                                        <span className="blog-status-badge draft">Draft</span>
                                                    )}
                                                </td>
                                                <td className="actions-cell">
                                                    <button type="button" className="action-icon-btn edit" title="Edit Post" onClick={() => editBlog(blog)}>
                                                        <i className="fa-solid fa-pencil"></i>
                                                    </button>
                                                    <button type="button" className="action-icon-btn delete" title="Delete Post" onClick={() => deleteBlogCall(blog._id)}>
                                                        <i className="fa-solid fa-trash-can"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
